import { ZodiacSign, Mood, ChineseZodiacSign } from './types';

export const DEFAULT_LIFE_EXPECTANCY = 90;

// Theme Colors for Zodiac Seasons (Tailwind-friendly or CSS var friendly)
export const ZODIAC_THEMES: Record<ZodiacSign, { primary: string; secondary: string; glow: string }> = {
  [ZodiacSign.Aries]: { primary: '#FF4D4D', secondary: '#FFA64D', glow: 'rgba(255, 77, 77, 0.4)' }, // Red/Orange
  [ZodiacSign.Taurus]: { primary: '#4ADE80', secondary: '#166534', glow: 'rgba(74, 222, 128, 0.4)' }, // Green/Earth
  [ZodiacSign.Gemini]: { primary: '#FACC15', secondary: '#A855F7', glow: 'rgba(250, 204, 21, 0.4)' }, // Yellow/Air
  [ZodiacSign.Cancer]: { primary: '#E2E8F0', secondary: '#94A3B8', glow: 'rgba(226, 232, 240, 0.4)' }, // Silver/Water
  [ZodiacSign.Leo]: { primary: '#F59E0B', secondary: '#B91C1C', glow: 'rgba(245, 158, 11, 0.4)' }, // Gold/Fire
  [ZodiacSign.Virgo]: { primary: '#84CC16', secondary: '#78350F', glow: 'rgba(132, 204, 22, 0.4)' }, // Olive/Earth
  [ZodiacSign.Libra]: { primary: '#F472B6', secondary: '#60A5FA', glow: 'rgba(244, 114, 182, 0.4)' }, // Pink/Blue
  [ZodiacSign.Scorpio]: { primary: '#7F1D1D', secondary: '#000000', glow: 'rgba(127, 29, 29, 0.5)' }, // Deep Red/Black
  [ZodiacSign.Sagittarius]: { primary: '#8B5CF6', secondary: '#C026D3', glow: 'rgba(139, 92, 246, 0.4)' }, // Purple/Fire
  [ZodiacSign.Capricorn]: { primary: '#57534E', secondary: '#1C1917', glow: 'rgba(87, 83, 78, 0.4)' }, // Brown/Earth
  [ZodiacSign.Aquarius]: { primary: '#22D3EE', secondary: '#3B82F6', glow: 'rgba(34, 211, 238, 0.4)' }, // Cyan/Air
  [ZodiacSign.Pisces]: { primary: '#60A5FA', secondary: '#A855F7', glow: 'rgba(96, 165, 250, 0.4)' }, // Dreamy Blue
};

export const MOOD_THEMES: Record<string, { color: string; glow: string; icon: string }> = {
    'Calm': { color: '#60A5FA', glow: 'rgba(96, 165, 250, 0.5)', icon: '😌' },
    'Confused': { color: '#A78BFA', glow: 'rgba(167, 139, 250, 0.5)', icon: '🤔' },
    'Excited': { color: '#F472B6', glow: 'rgba(244, 114, 182, 0.5)', icon: '🔥' },
    'Anxious': { color: '#34D399', glow: 'rgba(52, 211, 153, 0.5)', icon: '🌙' },
};

export const COMMON_TIMEZONES = [
  "UTC",
  "GMT",
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Toronto",
  "America/Vancouver",
  "America/Sao_Paulo",
  "Europe/London",
  "Europe/Paris",
  "Europe/Berlin",
  "Europe/Rome",
  "Europe/Madrid",
  "Europe/Moscow",
  "Africa/Cairo",
  "Africa/Johannesburg",
  "Asia/Dubai",
  "Asia/Karachi",
  "Asia/Kolkata",
  "Asia/Bangkok",
  "Asia/Shanghai",
  "Asia/Hong_Kong",
  "Asia/Tokyo",
  "Asia/Seoul",
  "Australia/Sydney",
  "Australia/Melbourne",
  "Pacific/Auckland",
  "Pacific/Honolulu"
];

export const ZODIAC_DATA: Record<ZodiacSign, { symbol: string; dateRange: string; element: string; description: string; rulingPlanet: string }> = {
  [ZodiacSign.Aries]: { 
    symbol: "♈", 
    dateRange: "Mar 21 - Apr 19", 
    element: "Fire", 
    rulingPlanet: "Mars",
    description: "Eager, dynamic, quick, and competitive. Aries is the first sign of the zodiac, and its presence always marks the beginning of something energetic and turbulent." 
  },
  [ZodiacSign.Taurus]: { 
    symbol: "♉", 
    dateRange: "Apr 20 - May 20", 
    element: "Earth", 
    rulingPlanet: "Venus",
    description: "Strong, dependable, sensual, and creative. Taurus is known for being practical, reliable, and grounded, valuing the fruits of hard labor." 
  },
  [ZodiacSign.Gemini]: { 
    symbol: "♊", 
    dateRange: "May 21 - Jun 20", 
    element: "Air", 
    rulingPlanet: "Mercury",
    description: "Expressive, quick-witted, and intellectual. Gemini represents two different personalities in one and you will never be sure which one you will face." 
  },
  [ZodiacSign.Cancer]: { 
    symbol: "♋", 
    dateRange: "Jun 21 - Jul 22", 
    element: "Water", 
    rulingPlanet: "Moon",
    description: "Intuitive, sentimental, compassionate, and protective. Deeply intuitive and sentimental, Cancer can be one of the most challenging zodiac signs to get to know." 
  },
  [ZodiacSign.Leo]: { 
    symbol: "♌", 
    dateRange: "Jul 23 - Aug 22", 
    element: "Fire", 
    rulingPlanet: "Sun",
    description: "Dramatic, outgoing, fiery, and self-assured. Leos are natural born leaders, confident, creative, and dominant, extremely difficult to resist." 
  },
  [ZodiacSign.Virgo]: { 
    symbol: "♍", 
    dateRange: "Aug 23 - Sep 22", 
    element: "Earth", 
    rulingPlanet: "Mercury",
    description: "Loyal, analytical, kind, and hardworking. Virgos are always paying attention to the smallest details and their deep sense of humanity makes them one of the most careful signs." 
  },
  [ZodiacSign.Libra]: { 
    symbol: "♎", 
    dateRange: "Sep 23 - Oct 22", 
    element: "Air", 
    rulingPlanet: "Venus",
    description: "Social, fair-minded, diplomatic, and gracious. Libra is a sign that is fascinated by balance and symmetry, constantly chasing justice and equality." 
  },
  [ZodiacSign.Scorpio]: { 
    symbol: "♏", 
    dateRange: "Oct 23 - Nov 21", 
    element: "Water", 
    rulingPlanet: "Pluto",
    description: "Passionate, stubborn, resourceful, and brave. Scorpio is a great leader, always aware of the situation and also features prominently in resourcefulness." 
  },
  [ZodiacSign.Sagittarius]: { 
    symbol: "♐", 
    dateRange: "Nov 22 - Dec 21", 
    element: "Fire", 
    rulingPlanet: "Jupiter",
    description: "Generous, idealistic, and great sense of humor. Sagittarius is one of the biggest travelers among all zodiac signs, open-minded and philosophical." 
  },
  [ZodiacSign.Capricorn]: { 
    symbol: "♑", 
    dateRange: "Dec 22 - Jan 19", 
    element: "Earth", 
    rulingPlanet: "Saturn",
    description: "Responsible, disciplined, and self-controlled. Capricorn is a sign that represents time and responsibility, and its representatives are traditional and often very serious by nature." 
  },
  [ZodiacSign.Aquarius]: { 
    symbol: "♒", 
    dateRange: "Jan 20 - Feb 18", 
    element: "Air", 
    rulingPlanet: "Uranus",
    description: "Deep, imaginative, original, and uncompromising. Aquarius people are eccentric and energetic, fighting for freedom, often shy and quiet but eccentric." 
  },
  [ZodiacSign.Pisces]: { 
    symbol: "♓", 
    dateRange: "Feb 19 - Mar 20", 
    element: "Water", 
    rulingPlanet: "Neptune",
    description: "Affectionate, empathetic, wise, and artistic. Pisces are very friendly, so they often find themselves in a company of very different people." 
  },
};

export const ZODIAC_ORDER = [
  ZodiacSign.Aries, ZodiacSign.Taurus, ZodiacSign.Gemini, ZodiacSign.Cancer,
  ZodiacSign.Leo, ZodiacSign.Virgo, ZodiacSign.Libra, ZodiacSign.Scorpio,
  ZodiacSign.Sagittarius, ZodiacSign.Capricorn, ZodiacSign.Aquarius, ZodiacSign.Pisces
];

export interface TarotCard {
  id: number;
  name: string;
  meaningUpright: string;
  meaningReversed: string;
}

export const TAROT_DECK: TarotCard[] = [

  { id: 0, name: "The Fool", meaningUpright: "New beginnings, innocence, spontaneity.", meaningReversed: "Recklessness, risk-taking, bad decisions." },

  { id: 1, name: "The Magician", meaningUpright: "Manifestation, resourcefulness, power.", meaningReversed: "Manipulation, poor planning, untapped talents." },

  { id: 2, name: "The High Priestess", meaningUpright: "Intuition, sacred knowledge, divine feminine.", meaningReversed: "Secrets, disconnected from intuition, withdrawal." },

  { id: 3, name: "The Empress", meaningUpright: "Femininity, beauty, nature, nurturing.", meaningReversed: "Creative block, dependence on others." },

  { id: 4, name: "The Emperor", meaningUpright: "Authority, establishment, structure.", meaningReversed: "Domination, excessive control, lack of discipline." },

  { id: 5, name: "The Hierophant", meaningUpright: "Spiritual wisdom, religious beliefs, conformity.", meaningReversed: "Personal beliefs, freedom, challenging status quo." },

  { id: 6, name: "The Lovers", meaningUpright: "Love, harmony, relationships, values alignment.", meaningReversed: "Self-love, disharmony, imbalance, misalignment." },

  { id: 7, name: "The Chariot", meaningUpright: "Control, willpower, success, action.", meaningReversed: "Self-discipline, opposition, lack of direction." },

  { id: 8, name: "Strength", meaningUpright: "Strength, courage, persuasion, influence.", meaningReversed: "Inner strength, self-doubt, low energy." },

  { id: 9, name: "The Hermit", meaningUpright: "Soul-searching, introspection, being alone.", meaningReversed: "Isolation, loneliness, withdrawal." },

  { id: 10, name: "Wheel of Fortune", meaningUpright: "Good luck, karma, life cycles, destiny.", meaningReversed: "Bad luck, resistance to change, breaking cycles." },

  { id: 11, name: "Justice", meaningUpright: "Justice, fairness, truth, cause and effect.", meaningReversed: "Unfairness, lack of accountability, dishonesty." },

  { id: 12, name: "The Hanged Man", meaningUpright: "Pause, surrender, letting go, new perspectives.", meaningReversed: "Delays, resistance, stalling, indecision." },

  { id: 13, name: "Death", meaningUpright: "Endings, change, transformation, transition.", meaningReversed: "Resistance to change, personal transformation." },

  { id: 14, name: "Temperance", meaningUpright: "Balance, moderation, patience, purpose.", meaningReversed: "Imbalance, excess, self-healing, re-alignment." },

  { id: 15, "name": "The Devil", "meaningUpright": "Shadow self, attachment, addiction, restriction.", "meaningReversed": "Releasing limiting beliefs, exploring dark thoughts." },

  { id: 16, name: "The Tower", meaningUpright: "Sudden change, upheaval, chaos, revelation.", meaningReversed: "Personal transformation, fear of change." },

  { id: 17, name: "The Star", meaningUpright: "Hope, faith, purpose, renewal, spirituality.", meaningReversed: "Lack of faith, despair, self-trust, disconnection." },

  { id: 18, name: "The Moon", meaningUpright: "Illusion, fear, anxiety, subconscious, intuition.", meaningReversed: "Release of fear, repressed emotion, inner confusion." },

  { id: 19, name: "The Sun", meaningUpright: "Positivity, fun, warmth, success, vitality.", meaningReversed: "Inner child, feeling down, overly optimistic." },

  { id: 20, name: "Judgement", meaningUpright: "Judgement, rebirth, inner calling, absolution.", meaningReversed: "Self-doubt, inner critic, ignoring the call." },

  { id: 21, name: "The World", meaningUpright: "Completion, integration, accomplishment, travel.", meaningReversed: "Seeking personal closure, short-cuts, delays." }

];



export const CHINESE_ZODIAC_DATA = {

  [ChineseZodiacSign.Rat]: {

    element: "Water",

    yinYang: "Yang",

    luckyColors: ["Blue", "Gold", "Green"],

    luckyNumbers: [2, 3],

    strengths: "Quick-witted, resourceful, versatile, kind.",

    challenges: "Timid, stubborn, wordy.",

    love: "Rats are charming and passionate in love, seeking a partner who understands their ambition.",

    career: "Their intelligence and adaptability make them excellent problem-solvers and business people.",

    bestMatches: ["Ox", "Dragon", "Monkey"],

    neutralMatches: ["Rat", "Snake", "Dog", "Pig"],

    challengingMatches: ["Horse", "Rooster", "Rabbit"]

  },

  [ChineseZodiacSign.Ox]: {

    element: "Earth",

    yinYang: "Yin",

    luckyColors: ["White", "Yellow", "Green"],

    luckyNumbers: [1, 4],

    strengths: "Diligent, dependable, strong, determined.",

    challenges: "Stubborn, poor at communication.",

    love: "The Ox is a faithful and steady partner, valuing honesty and a deep, lasting connection.",

    career: "With a strong work ethic, Oxen excel in fields that require diligence and reliability, like agriculture or engineering.",

    bestMatches: ["Rat", "Snake", "Rooster"],

    neutralMatches: ["Ox", "Tiger", "Dragon", "Horse", "Monkey"],

    challengingMatches: ["Goat", "Dog", "Pig"]

  },

    [ChineseZodiacSign.Tiger]: {

    element: "Wood",

    yinYang: "Yang",

    luckyColors: ["Blue", "Grey", "Orange"],

    luckyNumbers: [1, 3, 4],

    strengths: "Brave, confident, competitive, and unpredictable.",

    challenges: "Impulsive, irritable, and overindulgent.",

    love: "Tigers are passionate and charismatic in love, seeking partners who appreciate their adventurous spirit.",

    career: "Their natural leadership and courage make them excel in roles that require authority and decisiveness.",

    bestMatches: ["Horse", "Dog", "Pig"],

    neutralMatches: ["Rat", "Ox", "Rabbit", "Tiger"],

    challengingMatches: ["Monkey", "Snake"]

  },

  [ChineseZodiacSign.Rabbit]: {

    element: "Wood",

    yinYang: "Yin",

    luckyColors: ["Red", "Pink", "Purple", "Blue"],

    luckyNumbers: [3, 4, 6],

    strengths: "Quiet, elegant, kind, responsible.",

    challenges: "Timid, overly sentimental.",

    love: "Rabbits are gentle and romantic partners who value peace and harmony in relationships.",

    career: "Their artistic sense and attention to detail make them great in creative fields like design and literature.",

    bestMatches: ["Goat", "Dog", "Pig"],

    neutralMatches: ["Rat", "Rabbit", "Snake"],

    challengingMatches: ["Rooster", "Dragon"]

  },

  [ChineseZodiacSign.Dragon]: {

    element: "Earth",

    yinYang: "Yang",

    luckyColors: ["Gold", "Silver", "Greyish-white"],

    luckyNumbers: [1, 6, 7],

    strengths: "Confident, intelligent, enthusiastic.",

    challenges: "Arrogant, and short-tempered.",

    love: "Dragons are charismatic and energetic in love, seeking partners who can match their ambition and zest for life.",

    career: "As natural leaders, Dragons thrive in positions of power and influence.",

    bestMatches: ["Rat", "Monkey", "Rooster"],

    neutralMatches: ["Tiger", "Ox", "Snake", "Goat"],

    challengingMatches: ["Dog", "Rabbit"]

  },

  [ChineseZodiacSign.Snake]: {

    element: "Fire",

    yinYang: "Yin",

    luckyColors: ["Red", "Yellow", "Black"],

    luckyNumbers: [2, 8, 9],

    strengths: "Enigmatic, intelligent, wise.",

    challenges: "Jealous, and suspicious.",

    love: "Snakes are seductive and intuitive partners, forming deep and intense bonds.",

    career: "Their sharp minds make them excellent researchers, analysts, and investigators.",

    bestMatches: ["Ox", "Rooster"],

    neutralMatches: ["Rat", "Rabbit", "Dragon", "Snake", "Dog"],

    challengingMatches: ["Tiger", "Pig"]

  },

  [ChineseZodiacSign.Horse]: {

    element: "Fire",

    yinYang: "Yang",

    luckyColors: ["Yellow", "Green"],

    luckyNumbers: [2, 3, 7],

    strengths: "Animated, active, energetic.",

    challenges: "Impatient, and overly frank.",

    love: "Horses are free-spirited and passionate partners who need space and excitement.",

    career: "Their love for freedom and adventure makes them great entrepreneurs and performers.",

    bestMatches: ["Tiger", "Goat", "Dog"],

    neutralMatches: ["Rabbit", "Dragon", "Snake", "Monkey"],

    challengingMatches: ["Rat", "Ox", "Rooster"]

  },

  [ChineseZodiacSign.Goat]: {

    element: "Earth",

    yinYang: "Yin",

    luckyColors: ["Brown", "Red", "Purple"],

    luckyNumbers: [2, 7],

    strengths: "Gentle, calm, sympathetic.",

    challenges: "Pessimistic, and moody.",

    love: "Goats are gentle and caring partners who seek stability and romance.",

    career: "Their creative and nurturing nature suits professions like arts, teaching, and healthcare.",

    bestMatches: ["Rabbit", "Horse", "Pig"],

    neutralMatches: ["Tiger", "Dragon", "Monkey", "Goat"],

    challengingMatches: ["Ox", "Dog"]

  },

  [ChineseZodiacSign.Monkey]: {

    element: "Metal",

    yinYang: "Yang",

    luckyColors: ["White", "Blue", "Gold"],

    luckyNumbers: [4, 9],

    strengths: "Sharp, smart, curious.",

    challenges: "Mischievous, and arrogant.",

    love: "Monkeys are playful and engaging partners who love humor and intellectual connection.",

    career: "Their cleverness and adaptability make them thrive in fast-paced, challenging environments.",

    bestMatches: ["Rat", "Dragon"],

    neutralMatches: ["Ox", "Rabbit", "Horse", "Goat", "Rooster"],

    challengingMatches: ["Tiger", "Pig"]

  },

  [ChineseZodiacSign.Rooster]: {

    element: "Metal",

    yinYang: "Yin",

    luckyColors: ["Gold", "Brown", "Yellow"],

    luckyNumbers: [5, 7, 8],

    strengths: "Observant, hardworking, courageous.",

    challenges: "Arrogant, and impatient.",

    love: "Roosters are loyal and honest partners who appreciate order and appearance.",

    career: "Their meticulous nature makes them excellent in roles that require attention to detail.",

    bestMatches: ["Ox", "Dragon", "Snake"],

    neutralMatches: ["Goat", "Monkey", "Pig"],

    challengingMatches: ["Rat", "Rabbit", "Horse", "Dog"]

  },

  [ChineseZodiacSign.Dog]: {

    element: "Earth",

    yinYang: "Yang",

    luckyColors: ["Red", "Green", "Purple"],

    luckyNumbers: [3, 4, 9],

    strengths: "Lovely, honest, prudent.",

    challenges: "Stubborn, and overly critical.",

    love: "Dogs are loyal, and faithful partners who value honesty and trust above all.",

    career: "Their strong sense of justice makes them suitable for careers in law, teaching, and public service.",

    bestMatches: ["Tiger", "Rabbit", "Horse"],

    neutralMatches: ["Rat", "Snake", "Monkey", "Pig"],

    challengingMatches: ["Ox", "Dragon", "Goat", "Rooster"]

  },

  [ChineseZodiacSign.Pig]: {

    element: "Water",

    yinYang: "Yin",

    luckyColors: ["Yellow", "Gray", "Brown"],

    luckyNumbers: [2, 5, 8],

    strengths: "Compassionate, generous, diligent.",

    challenges: "Naive, and short-tempered.",

    love: "Pigs are sincere and loving partners who enjoy the simple comforts of a stable home life.",

    career: "Their generosity and diligence make them well-suited for social work and philanthropy.",

    bestMatches: ["Tiger", "Rabbit", "Goat"],

    neutralMatches: ["Rat", "Ox", "Dragon", "Rooster", "Dog"],

    challengingMatches: ["Snake", "Monkey"]

  }

};